//@ts-nocheck

import React, { useEffect, useState } from "react";
import {
  Box,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Link,
  Button,
  CardMedia,
  Typography,
  CircularProgress,
} from "@mui/material";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import { CustomPaginationCommon } from "modules/Users/components";
import { useNavigate, useParams } from "react-router-dom";
import "../../../../styles/earningTable.css";
import { EditThresholdModal } from "./EditThresholdModal";
import { AddPayoutAccountModal } from "./AddPayoutAccountModal";
import AddAccountDetailModal from "./AddAccountDetailModal";
import AddIcon from "@mui/icons-material/Add";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { useAuth } from "context/authContext";
import { PurchasesService } from "services/PurchasesService";
import { PayoutService } from "services/PayoutService";
import {
  createPayout,
  createStripeAccount,
  retriveStripeAccount,
  getStripeAccountDetails,
  completeStripeOnboarding,
} from "utils/firebaseFunctions";
import { UserService } from "services/UserService";

interface PaymentDetails {
  id?: string;
  routingNumber: string;
  accountNumber: string;
  confirmAccountNumber?: string;
}

const EarningTable = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const { userType } = useParams();
  const [isLoading, setIsLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(7);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [mode, setMode] = useState<"add" | "edit">("add");
  const [open, setOpen] = useState(false);
  const [paymentDetails, setPaymentDetails] = useState<PaymentDetails | null>(
    null
  );

  const [isPaymentVisible, setIsPaymentVisible] = useState(false);
  const [contentColor, setContentColor] = useState("#797979");
  const [initialValues, setInitialValues] = useState<PaymentDetails>({
    routingNumber: "",
    accountNumber: "",
    confirmAccountNumber: "",
  });
  const [totalEarningsData, setTotalEarningsData] = useState<
    number | undefined
  >(undefined);
  const [salesData, setSalesData] = useState([]);

  console.log("salesData:", salesData);


  useEffect(() => {
    const fetchAndUpdateBalance = async () => {
      if (!currentUser?.uid) return;

      try {
        await PayoutService.updateUserBalanceFromPurchases(currentUser.uid); // ✅ Call update function
      } catch (error) {
        console.error("Failed to update user balance:", error);
      }
    };

    fetchAndUpdateBalance();
  }, [currentUser]);

  const currentBalance = totalEarningsData || 0;


  const handleOpen = (mode: "add" | "edit") => {
    setMode(mode);
    if (mode === "add") {
      setInitialValues({
        routingNumber: "",
        accountNumber: "",
        confirmAccountNumber: "",
      });
    } else if (mode === "edit" && paymentDetails) {
      setInitialValues({
        routingNumber: paymentDetails.routingNumber || "",
        accountNumber: paymentDetails.accountNumber || "",
        confirmAccountNumber: paymentDetails.confirmAccountNumber || "",
      });
    }
    setOpen(true);
  };

  const handleAddPayoutAccount = async () => {
    if (!currentUser) {
      alert("You must be logged in to add a payout account.");
      return;
    }

    setIsLoading(true);

    try {
      let stripeAccountId = currentUser?.userInfo?.stripeAccountId;
      let onboardingComplete = currentUser?.userInfo?.stripeOnboardingComplete;

      // Check if user has a Stripe account but onboarding is not complete
      if (stripeAccountId && !onboardingComplete) {
        console.log("Onboarding not complete. Redirecting to onboarding...");
        await retriveStripeAccount(stripeAccountId);
        return; // Exit early as user is redirected to complete onboarding
      }

      // If no Stripe account exists, create a new one
      if (!stripeAccountId) {
        console.log("No existing Stripe account. Creating a new one...");
        await createStripeAccount(currentUser.uid, currentUser.email);
        // Exit early as user is redirected to onboarding
        return;
      }

      // If we get here, user has a Stripe account and onboarding is complete

      // Get account details from Stripe
      const accountDetails = await getStripeAccountDetails(stripeAccountId);

      if (!accountDetails.success) {
        throw new Error(
          accountDetails.error || "Failed to get account details"
        );
      }

      const bankAccount = accountDetails.bankAccounts?.[0];

      if (!bankAccount) {
        alert("No bank account found. Please add a bank account in Stripe.");
        await retriveStripeAccount(stripeAccountId);
        return;
      }

      // Store the payout account in Firestore
      await PayoutService.createPayout({
        userId: currentUser.uid,
        stripeAccountId: stripeAccountId,
        accountNumber: bankAccount.last4 || "****",
        routingNumber: bankAccount.routing_number || "****",
        bankName: bankAccount.bank_name || "Unknown Bank",
        isDefault: true,
        isActive: true,
      });

      // Update local state
      setPaymentDetails({
        accountNumber: bankAccount.last4 || "****",
        routingNumber: bankAccount.routing_number || "****",
      });
      setIsPaymentVisible(true);
    } catch (error) {
      console.error("Error handling Stripe account:", error);
      alert("Failed to retrieve or create Stripe account. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => setOpen(false);

  const [thresholdDetails, setThresholdDetails] = useState({
    autoPayThresholdAmount: "",
    autoPay: true,
  });

  useEffect(() => {
    const fetchSellerContent = async () => {
      try {
        if (!currentUser?.uid) return;

        // Fetch purchases where the user is the seller/creator
        const sales = await PayoutService.getPurchasesBySellerId(
          currentUser.uid
        );
        setSalesData(sales);
        // Calculate total earnings
        const total = sales.reduce((sum, sale) => sum + (sale.amount || 0), 0);

        setTotalEarningsData(total); // Update the state with total earnings
      } catch (error) {
        console.error("Error fetching seller's content:", error);
      }
    };

    fetchSellerContent();
  }, [currentUser]);

  const handleWithdraw = async () => {
    if (!currentUser) {
      alert("You must be logged in to withdraw.");
      return;
    }

    if (currentBalance < 150) {
      // ✅ Ensure withdrawal meets minimum threshold
      alert("Minimum withdrawal amount is $150.");
      return;
    }

    await createPayout(currentUser.uid, currentBalance);

    // ✅ Update balance after withdrawal
    await PayoutService.updateUserBalanceFromPurchases(currentUser.uid);
  };

  useEffect(() => {
    const fetchPayoutDetails = async () => {
      try {
        const userId = currentUser?.uid;
        if (!userId) return;

        const payouts = await PayoutService.getPayoutsByUserId(userId);

        if (payouts.length > 0) {
          const payout = payouts[0]; // Assume only one payout account per user

          // Convert numbers to strings for compatibility
          setPaymentDetails({
            id: payout.id, // Include Firestore document ID
            routingNumber: payout.routingNumber.toString(),
            accountNumber: payout.accountNumber.toString(),
            confirmAccountNumber: "", // Handle undefined case
          });

          setIsPaymentVisible(true);
          setContentColor("");
        } else {
          setIsPaymentVisible(false);
        }
      } catch (error) {
        console.error("Error fetching payout details:", error);
      }
    };

    fetchPayoutDetails();
  }, [currentUser]);

  const handleEditThreshold = () => {
    setIsEditModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsEditModalOpen(false);
  };
  const handleAddModal = () => {
    setAddModalOpen(true);
  };
  const handleDetailCloseModal = () => {
    setDetailModalOpen(false);
  };
  const handleDetailModal = () => {
    setDetailModalOpen(true);
  };

  const handleCloseAddModal = () => {
    setAddModalOpen(false);
  };

  const handleSubmitModal = (data: any) => {
    setThresholdDetails(data);
  };

  const handleSubmitDetailModal = async (data: PaymentDetails) => {

    try {
      if (mode === "edit" && paymentDetails?.id) {
        // Update existing document
        await PayoutService.updatePayout(paymentDetails.id, {
          routingNumber: Number(data.routingNumber),
          accountNumber: Number(data.accountNumber),
        });
        console.log("Payout account successfully updated!");
      } else {
        // Create a new document
        await PayoutService.createPayout({
          routingNumber: Number(data.routingNumber),
          accountNumber: Number(data.accountNumber),
          userId: currentUser?.uid || "",
        });
        console.log("Payout account successfully created!");
      }

      setPaymentDetails(data); // Update local state
      setIsPaymentVisible(true); // Show updated payment info
      setContentColor(""); // Reset styling
      handleClose(); // Close the modal after success
    } catch (error) {
      console.error("Error updating or creating payout account:", error);
    }
  };
  // Sample data
  const payments = [
    {
      transactionID: "#123412451",
      date: "June 1, 2024",
      contentName: "Neuropsychology: Brain and Behavior",
      email: "serraozk@mail.com",
      totalPaid: "$30",
      status: "Complete",
    },
    {
      transactionID: "#123412451",
      date: "June 1, 2024",
      contentName: "Neuropsychology: Brain and Behavior",
      email: "serraozk@mail.com",
      totalPaid: "$30",
      status: "Pending",
    },
    {
      transactionID: "#123412451",
      date: "June 1, 2024",
      contentName: "Neuropsychology: Brain and Behavior",
      email: "serraozk@mail.com",
      totalPaid: "$30",
      status: "Complete",
    },
    {
      transactionID: "#123412451",
      date: "June 1, 2024",
      contentName: "Neuropsychology: Brain and Behavior",
      email: "serraozk@mail.com",
      totalPaid: "$30",
      status: "Pending",
    },
    {
      transactionID: "#123412451",
      date: "June 1, 2024",
      contentName: "Neuropsychology: Brain and Behavior",
      email: "serraozk@mail.com",
      totalPaid: "$30",
      status: "Complete",
    },
    {
      transactionID: "#123412451",
      date: "June 1, 2024",
      contentName: "Neuropsychology: Brain and Behavior",
      email: "serraozk@mail.com",
      totalPaid: "$30",
      status: "Complete",
    },
    {
      transactionID: "#123412451",
      date: "June 1, 2024",
      contentName: "Neuropsychology: Brain and Behavior",
      email: "serraozk@mail.com",
      totalPaid: "$30",
      status: "Pending",
    },
    {
      transactionID: "#123412451",
      date: "June 1, 2024",
      contentName: "Neuropsychology: Brain and Behavior",
      email: "serraozk@mail.com",
      totalPaid: "$30",
      status: "Complete",
    },
    // Repeat this object for more rows
  ];

  const handleChangePage = (
    event: any,
    newPage: React.SetStateAction<number>
  ) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: { target: { value: string } }) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Add this useEffect to handle the callback from Stripe
  useEffect(() => {
    const handleStripeCallback = async () => {
      if (!currentUser?.uid) return;

      // Get URL parameters
      const urlParams = new URLSearchParams(window.location.search);
      const stripeAccountId = urlParams.get("stripeAccountId");

      // Check if this is a Stripe callback
      if (stripeAccountId) {
        try {
          setIsLoading(true);

          // Update the user document directly from the client
          await UserService.updateUser(currentUser.uid, {
            stripeAccountId: stripeAccountId,
            stripeOnboardingComplete: true,
          });

          // Clear URL parameters to prevent reprocessing
          window.history.replaceState({}, document.title, "/my-earnings");

          // Show success message
          alert("Your Stripe account has been successfully connected!");

          // Refresh the page to update the UI
          window.location.reload();
        } catch (error) {
          console.error("Error updating user with Stripe account:", error);
          alert("An error occurred while connecting your Stripe account.");
        } finally {
          setIsLoading(false);
        }
      }
    };

    handleStripeCallback();
  }, [currentUser]);

  return (
    <Grid sx={styleSheet.mainContainer}>
      <Box sx={styleSheet.userText}>My Earnings</Box>
      <Grid
        item
        xs={10}
        display={"flex"}
        justifyContent={{ xs: "center", md: "start" }}
        flexWrap={{ xs: "wrap", md: "nowrap" }}
        gap={{ xs: "15px", md: 5 }}
      >
        <Box sx={{ ...styleSheet.withdrawBox, color: contentColor }}>
          <Box display={"flex"} flexDirection={"column"} gap={2}>
            <Box
              fontWeight={600}
              fontSize={{ xs: "33.81px", md: "38px" }}
              // color={"#1DA944"}
              sx={{ color: contentColor ? "#797979" : "#1DA944" }}
            >
              $
              {totalEarningsData !== undefined
                ? `${totalEarningsData}`
                : "Loading..."}
            </Box>
            <Box>Current Balance</Box>
            <Button
              sx={{
                ...styleSheet.withdrawBtn,
              }}
              // onClick={handleAddModal}
              onClick={handleWithdraw}
              disabled={
                totalEarningsData === undefined || totalEarningsData < 150
              }
            >
              Withdraw
            </Button>
          </Box>
          <Box>
            <Box sx={styleSheet.iconContainer}>
              <img src="/assets/icons/Vector.png" alt="Bank Icon" />
            </Box>
          </Box>
        </Box>
        <Box
          // sx={styleSheet.editContainer}
          sx={{
            ...styleSheet.editContainer,
            color: contentColor ? "#797979" : "",
          }}
        >
          <Box display={"flex"} flexDirection={"column"} gap={2}>
            <Box fontWeight={600} fontSize={"38px"} color={"#797979"}>
              $ 150
            </Box>
            <Box>Auto Pay Threshold</Box>
            {/* <Button
              sx={{
                ...styleSheet.withdrawBtn,
                backgroundColor: contentColor ? "#797979" : "#233f91",
              }}
              onClick={handleEditThreshold}
            >
              Edit
            </Button> */}
          </Box>
          <EditThresholdModal
            open={isEditModalOpen}
            handleClose={handleCloseModal}
            handleSubmit={handleSubmitModal}
            initialValues={thresholdDetails}
          />
          <AddPayoutAccountModal
            open={addModalOpen}
            handleClose={handleCloseAddModal}
          />
          <AddAccountDetailModal
            open={open}
            handleClose={handleClose}
            handleSubmit={handleSubmitDetailModal}
            //@ts-ignore
            initialValues={initialValues}
            mode={mode}
          />
          <Box>
            <Box>
              <img src="/assets/icons/affright.png" alt="Bank Icon" />
            </Box>
          </Box>
        </Box>
      </Grid>
      <Grid item xs={10} ml={{ xs: "10px", md: "0px" }}>

        <Box sx={styleSheet.payoutContainer}>
          <Box sx={styleSheet.payoutText}>Payout Account</Box>
          {isPaymentVisible ? (
            <></>
          ) : (
            <Button
              onClick={handleAddPayoutAccount}
              // onClick={() => handleOpen("add")}
              sx={styleSheet.addNewBtn}
            >
              {isLoading ? (

                <CircularProgress size={20} color="inherit" />
              ) : (
                <AddIcon />

              )}

              {!isLoading && "Add a Payout Account"}
            </Button>
          )}
        </Box>



        {isPaymentVisible ? (
          <Box sx={styleSheet.bankAccountContainer}>
            <Box
              display={"flex"}
              alignItems={"center"}
              gap={{ xs: 2, md: 8 }}
              flexWrap={{ xs: "wrap", md: "nowrap" }}
            >
              {/* <img src="/assets/icons/affPaymentbg.png" alt="affPaymentbg" style={{ paddingLeft: "20px" }} /> */}
              <CardMedia
                sx={{
                  width: { xs: "127.76px", md: "148px" },
                  height: { xs: "85px", md: "98px" },
                  ml: { xs: "0px", md: "20px" },
                  objectFit: "cover",
                  mx: { xs: "auto", md: "0px" },
                }}
                image={"/assets/icons/payoutCard.svg"}
              />
              <Box
                display={"flex"}
                flexDirection={"column"}
                gap={{ xs: "0px", md: 1 }}
              >
                <Box
                  fontWeight={400}
                  fontSize={"17px"}
                  color={"rgba(0, 0, 0, 0.8)"}
                >
                  Bank Account
                </Box>
                <Box fontWeight={700} fontSize={"18px"} color={"#595959"}>
                  {paymentDetails?.accountNumber
                    ? `**** **** **** ${paymentDetails.accountNumber.slice(-4)}`
                    : "**** **** **** 6783"}
                </Box>
              </Box>
              <Box
                display={"flex"}
                flexDirection={"column"}
                gap={{ xs: "0px", md: 1 }}
              >

              </Box>
            </Box>
            {/* <Link
              className="rowLinkBluePayout"
              onClick={() => handleOpen("edit")}
            >
              Edit Payment Information
            </Link> */}
          </Box>
        ) : (
          ""
        )}
      </Grid>
      <Box
        display={{ xs: "none", md: "flex" }}
        flexDirection={"column"}
        gap={"20px"}
        ml={{ xs: "10px", md: "0px" }}
      >
        <Box sx={styleSheet.payoutHistory}>Payout History</Box>
        {isPaymentVisible ? (
          <Grid item xs={10}>
            <div
              className=""
              style={{ display: "flex", flexDirection: "column", gap: "35px" }}
            >
              <table className="tableHeader">
                <td className="transitionId">Transaction ID</td>
                <td className="date">Date</td>
                <td className="conatent">Content Name</td>
                <td className="emailTotal">Email</td>
                <td className="emailTotal">Total Paid</td>
                <td className="status">Status</td>
              </table>

              <div
                className="scrollable-container"
                style={{
                  backgroundColor: "#fff",
                  borderRadius: "15px",
                  padding: "24px",
                  display: "flex",
                  flexDirection: "column",
                  gap: "28px",
                }}
              >
                {salesData
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((payment) => (
                    <table key={payment.id} className="rowHeader">
                      <td className="rowTrasitionIdp">
                        {payment.transactionId}
                      </td>

                      <td className="rowDatep"> {payment?.updatedAt
                        ?.toDate()
                        .toLocaleDateString("en-US", {
                          month: "short",
                          day: "2-digit",
                          year: "numeric",
                        })}</td>
                      <td className="rowContentp">{payment?.contentData?.title}</td>
                      <td className="rowEmailTotalp">{payment?.creatorData?.email}</td>
                      <td className="rowEmailTotalp">{payment?.contentData?.price}</td>
                      <div className="rowColumnBtn">
                        <Button
                          sx={{
                            ...styleSheet.rowButton,
                            borderColor:
                              payment?.currentStatus === "Complete"
                                ? "#2BC155"
                                : "#FF9548",
                            color:
                              payment?.currentStatus === "Complete"
                                ? "#1DA944"
                                : "#FF9548",
                            borderWidth: "1px",
                            borderStyle: "solid",

                          }}
                        >
                          {payment.currentStatus}
                        </Button>
                      </div>
                    </table>
                  ))}
              </div>
            </div>
            <div
              style={{
                marginTop: "20px",
                display: "flex",
                justifyContent: "center",
              }}
            >
              <CustomPaginationCommon
                count={salesData.length}
                page={page}
                rowsPerPage={rowsPerPage}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            </div>
          </Grid>
        ) : (
          <Box
            display={"flex"}
            gap={1}
            alignItems={"center"}
            fontWeight={400}
            fontSize={22}
            color={"#797979"}
          >
            <ErrorOutlineIcon />
            <Box>No data to display</Box>
          </Box>
        )}
      </Box>
      {/* /// mobile screen /// */}
      <Box
        display={{ xs: "flex", md: "none" }}
        flexDirection={"column"}
        gap={"20px"}
      >
        <Box
          fontWeight={500}
          fontSize="28px"
          color="#233F91"
          display="flex"
          alignItems="center"
        >
          Payment History
        </Box>
        <Box
          bgcolor={"#fff"}
          border={"1px solid #E5E5E5"}
          borderRadius={"15px"}
          padding={"10px"}
          display="flex"
          flexDirection={"column"}
          gap={"12px"}
        >
          {salesData
            .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
            .map((payment, index) => (
              <Box
                key={index}
                border={"1px solid #E5E5E5"}
                borderRadius={"15px"}
                padding={"10px"}
                pt={3}
                display={"flex"}
                flexDirection={"column"}
                gap={"18px"}
              >
                <Box display={"flex"} gap={2} alignItems={"center"}>
                  <Typography
                    fontWeight={700}
                    fontSize={"13.29px"}
                    color={"#233F91"}
                  >
                    Transaction ID
                  </Typography>
                  <Typography
                    fontWeight={400}
                    fontSize={"17px"}
                    color={"#313131"}
                  >
                    {payment.transactionId}
                  </Typography>
                </Box>
                <Box display={"flex"} gap={0} alignItems={"center"}>
                  <Typography
                    fontWeight={700}
                    fontSize={"13.29px"}
                    color={"#233F91"}
                    width={"150px"}
                  >
                    Content Name
                  </Typography>

                  <Typography
                    fontWeight={400}
                    fontSize={"17px"}
                    color={"#313131"}
                    className="ellipsis"
                  // onClick={handleToggleText} // Replace with your custom function to show full text
                  >
                    {payment?.contentData?.title}
                  </Typography>
                </Box>
                <Box display={"flex"} gap={4} alignItems={"center"}>
                  <Typography
                    fontWeight={700}
                    fontSize={"13.29px"}
                    color={"#233F91"}
                  >
                    Date
                  </Typography>
                  <Typography
                    fontWeight={400}
                    fontSize={"17px"}
                    color={"#313131"}
                  >
                    {payment?.updatedAt
                      ?.toDate()
                      .toLocaleDateString("en-US", {
                        month: "short",
                        day: "2-digit",
                        year: "numeric",
                      })}
                  </Typography>
                </Box>

                <Box display={"flex"} gap={"30px"} alignItems={"center"}>
                  <Typography
                    fontWeight={700}
                    fontSize={"13.29px"}
                    color={"#233F91"}
                  >
                    Email
                  </Typography>
                  <Typography
                    fontWeight={400}
                    fontSize={"17px"}
                    color={"#313131"}
                  >
                    {payment?.creatorData?.email}
                  </Typography>
                </Box>
                <Box display={"flex"} gap={2} alignItems={"center"}>
                  <Typography
                    fontWeight={700}
                    fontSize={"13.29px"}
                    color={"#233F91"}
                  >
                    Total Paid
                  </Typography>
                  <Typography
                    fontWeight={400}
                    fontSize={"17px"}
                    color={"#313131"}
                  >
                    {payment?.contentData?.price}
                  </Typography>
                </Box>
                <Box pb={1}>
                  <Typography
                    fontWeight={700}
                    fontSize={"13.29px"}
                    color={"#233F91"}
                  >
                    Status
                  </Typography>

                  <Box
                    display={"flex"}
                    gap={2}
                    alignItems={"center"}
                    justifyContent={"center"}
                    mr={3}
                  >
                    <Box>
                      <Button
                        sx={{
                          ...styleSheet.rowButton,
                          borderColor:
                            payment.status === "Complete"
                              ? "#2BC155"
                              : "#FF9548",
                          color:
                            payment.status === "Complete"
                              ? "#1DA944"
                              : "#FF9548",
                          borderWidth: "1px",
                          borderStyle: "solid",
                        }}
                      >
                        {payment.status}
                      </Button>
                    </Box>
                  </Box>
                </Box>
              </Box>
            ))}
          <Box>
            <CustomPaginationCommon
              count={payments.length}
              page={page}
              rowsPerPage={rowsPerPage}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </Box>
        </Box>
      </Box>
    </Grid>
  );
};

export default EarningTable;

const styleSheet = {
  mainContainer: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    gap: { xs: 2, md: 5 },
    px: { xs: 2, md: "55px" },
  },
  userText: {
    pt: { xs: 15, md: 3 },
    ml: { xs: "10px", md: "0px" },
    fontWeight: 700,
    fontSize: "35px",
    color: "#233F91",
    display: "flex",
    alignItems: "center",
  },
  rowButton: {
    borderRadius: "30px",
    // width: '154px',
    width: "130px",
    height: "49px",
    textTransform: "none",
    backgroundColor: "transparent",
    fontSize: "18px",
    mr: 3,
    fontWeight: 700,
    "&:hover": {
      backgroundColor: "none",
    },
  },
  imgContainer: {
    width: "48px",
    height: "48px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#f5f5f5",
    borderRadius: "50%",
  },
  addNewBtn: {
    borderRadius: { xs: "10px", md: "15px" },
    width: { xs: "150px", md: "216px" },
    height: { xs: "40px", md: "48px" },
    textTransform: "none",
    backgroundColor: "#ffffff",
    border: "1px solid #233f91",
    color: "#233f91",
    fontSize: { xs: "10px", md: "15px" },
    fontWeight: 700,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "10px",
  },
  withdrawBox: {
    bgcolor: "#fff",
    width: { xs: "277.65px", md: "307px" },
    borderRadius: { xs: "26.69px", md: "15px" },
    p: { xs: 3, md: 2 },
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 5,
    // boxShadow: 1,
    boxShadow: {
      xs: "0px 4px 10px rgba(0, 0, 0, 0.1)",
      md: "0px 4px 15px rgba(0, 0, 0, 0.15)",
    },
  },
  withdrawBtn: {
    textTransform: "capitalize",
    width: "144px",
    height: "48px",
    borderRadius: "15px",
    fontSize: "16px",
    color: "#fff",
    backgroundColor: "#233F91", // Default color when enabled
    "&:hover": {
      backgroundColor: "#1a2d6f",
    },
    "&.Mui-disabled": {
      backgroundColor: "#797979", // Disabled color
      color: "#fff", // Ensure text remains visible
      opacity: 1, // Override Material-UI default opacity for disabled buttons
    },
  },

  iconContainer: {
    width: "48px",
    height: "48px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    bgcolor: "#f5f5f5",
    borderRadius: "50%",
  },
  editContainer: {
    bgcolor: "#fff",
    width: { xs: "277.65px", md: "307px" },
    borderRadius: { xs: "26.69px", md: "15px" },
    p: { xs: 3, md: 2 },
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 5,
    // boxShadow: 2,
    boxShadow: {
      xs: "0px 4px 10px rgba(0, 0, 0, 0.1)",
      md: "0px 4px 15px rgba(0, 0, 0, 0.15)",
    },
  },
  bankAccountContainer: {
    // display: "flex", bgcolor: "#fff", borderRadius: "15px", p: 2, justifyContent: 'space-between', alignItems: "center",
    display: "flex",
    bgcolor: "#fff",
    borderRadius: "15px",
    border: { xs: "1px solid #D4D4D4", md: "none" },
    p: 2,
    justifyContent: "space-between",
    alignItems: "center",
    width: { xs: "209px", md: "100%" },
    flexWrap: { xs: "wrap", md: "nowrap" },
    gap: { xs: "20px", md: "0px" },
  },
  payoutContainer: {
    display: "flex",
    pb: 4,
    justifyContent: "space-between",
    alignItems: "center",
    // flexWrap: { xs: "wrap", md: "nowrap" }
  },
  payoutText: {
    fontWeight: 500,
    fontSize: { xs: "20px", md: "28px" },
    color: "#233F91",
    display: "flex",
    alignItems: "center",
  },
  payoutHistory: {
    pb: 2,
    fontWeight: 500,
    fontSize: { xs: "20px", md: "28px" },
    color: "#233F91",
    display: "flex",
    alignItems: "center",
  },
};
